import { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import { RequestFilters, RangeFilter } from 'utils/types/filters';
import RequestsTableComponent from 'Organisms/RequestsTable/RequestsTable';
import RequestsTableFooter from 'Organisms/RequestsTable/RequestsTableFooter';
import RequestsTableOperationHeader from 'Organisms/RequestsTable/RequestsTableOperationHeader/RequestsTableOperationHeader';
import {
  requestsSelectors,
  setFilterParameter,
} from 'state/normalized/requests/requestsSlice';
import useRequestsTable from 'Hooks/Requests/useRequestsTable';
import useFilteredStatuses from 'Hooks/Requests/useFilteredStatuses/useFilteredStatuses';
import {
  selectFiltersSettingsByType,
  createFilterSetting,
  updateFilterSetting,
} from 'state/Settings/Filters/FiltersSlice';
import { REQUEST_TABLE_FILTERS, SETTINGS_FILTERS } from 'utils/constants';
import { selectOrganizationSettings } from 'state/Organization/organizationSlice';
import { selectSelectAll } from 'state/ExportRequests/ExportRequestsSlice';
import { QUICK_FILTERS } from 'utils/constants/request';
import {
  selectIsBusinessUser,
  selectUserBusinessTeams,
} from 'state/User/userSlice';
import RequestsSidePanelFilters from '../RequestsSidePanelFilters/RequestsSidePanelFilters';

const RequestsTable = () => {
  const dispatch = useDispatch();

  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [areFiltersApplied, setAreFiltersApplied] = useState<boolean>(false);
  const [areStandardFiltersApplied, setAreStandardFiltersApplied] =
    useState(false);
  const [areFiltersLoading, setAreFiltersLoading] = useState<boolean>(false);
  const [canExportRequests, setCanExportRequests] = useState<boolean>(false);
  const [selectedRowsIds, setSelectedRowsIds] = useState<string[]>([]);

  const filtersSettings = useSelector(
    selectFiltersSettingsByType(SETTINGS_FILTERS.REQUESTS_TABLE)
  );
  const organizationSettings = useSelector(selectOrganizationSettings);
  const userBusinessTeams = useSelector(selectUserBusinessTeams);
  const isBusinessUser = useSelector(selectIsBusinessUser);
  const totalCount = useSelector(requestsSelectors.selectTotalRequests);
  const loadedCount = useSelector(requestsSelectors.selectLoadedRequests);
  const selectAll = useSelector(selectSelectAll);

  const isInBusinessTeam = userBusinessTeams!.length > 0;
  const isTeamTabEnabled = get(organizationSettings, 'teamRequestsTab', false);
  const canBusinessUserSeeTeamRequests =
    isBusinessUser && isInBusinessTeam && isTeamTabEnabled;

  const appliedFilters = useMemo(
    () =>
      filtersSettings
        ? (filtersSettings.settings as RequestFilters)
        : ({} as RequestFilters),
    [filtersSettings]
  );

  const { statuses, shouldFetchData } = useFilteredStatuses(appliedFilters);

  const {
    requests,
    status,
    canFetchMore,
    onFetchMore,
    searchRequests,
    isSearchApplied,
    lastPatchedRequestId,
  } = useRequestsTable({
    filterType: SETTINGS_FILTERS.REQUESTS_TABLE,
    statuses,
    filterAppliedRequesterIds: appliedFilters[
      REQUEST_TABLE_FILTERS.REQUESTER
    ] as string[],
    projectIds: appliedFilters[REQUEST_TABLE_FILTERS.PROJECTS] as string[],
    filterAppliedOwnerIds: appliedFilters[
      REQUEST_TABLE_FILTERS.OWNERS
    ] as string[],
    filterAppliedBusinessUnitIds: appliedFilters[
      REQUEST_TABLE_FILTERS.BUSINESS_TEAMS
    ] as string[],
    numberOfProjects: appliedFilters[
      REQUEST_TABLE_FILTERS.LINKED_PROJECTS
    ] as string[],
    filterAppliedPriority: appliedFilters[
      REQUEST_TABLE_FILTERS.PRIORITY
    ] as string[],
    formIds: appliedFilters[REQUEST_TABLE_FILTERS.FORM] as string[],
    updatedAt: appliedFilters[REQUEST_TABLE_FILTERS.UPDATED_AT] as RangeFilter,
    createdAt: appliedFilters[REQUEST_TABLE_FILTERS.CREATED_AT] as RangeFilter,
    submittedAt: appliedFilters[
      REQUEST_TABLE_FILTERS.SUBMITTED_AT
    ] as RangeFilter,
    decisionDate: appliedFilters[
      REQUEST_TABLE_FILTERS.DECISION_DATE
    ] as RangeFilter,
    myRequests: (appliedFilters[REQUEST_TABLE_FILTERS.MY_REQUESTS] ||
      (!canBusinessUserSeeTeamRequests && isBusinessUser)) as boolean,
    shouldFetchData,
    archived: appliedFilters[REQUEST_TABLE_FILTERS.ARCHIVED] as boolean,
  });

  useEffect(() => {
    if (!appliedFilters) return;

    // CGP-268: persist `status: []` as-is when the applied filters intersect
    // to no fetchable statuses. Refetch call sites (column sort, side panel,
    // unarchive) spread this slice into getRequests; the thunk short-circuits
    // an explicitly-empty status list into an empty page, whereas leaving the
    // slice at its initial {} would make those refetches fall back to the
    // default status filter and fill an intentionally-empty table.
    dispatch(
      setFilterParameter({
        status: statuses,
        filterParams: {
          requesterIds: appliedFilters[
            REQUEST_TABLE_FILTERS.REQUESTER
          ] as string[],
          projectIds: appliedFilters[
            REQUEST_TABLE_FILTERS.PROJECTS
          ] as string[],
          ownerIds: appliedFilters[REQUEST_TABLE_FILTERS.OWNERS] as string[],
          businessUnitIds: appliedFilters[
            REQUEST_TABLE_FILTERS.BUSINESS_TEAMS
          ] as string[],
          numberOfProjects: appliedFilters[
            REQUEST_TABLE_FILTERS.LINKED_PROJECTS
          ] as string[],
          ldPriority: appliedFilters[
            REQUEST_TABLE_FILTERS.PRIORITY
          ] as string[],
          formIds: appliedFilters[REQUEST_TABLE_FILTERS.FORM] as string[],
          updatedAt: appliedFilters[
            REQUEST_TABLE_FILTERS.UPDATED_AT
          ] as RangeFilter,
          createdAt: appliedFilters[
            REQUEST_TABLE_FILTERS.CREATED_AT
          ] as RangeFilter,
          submittedAt: appliedFilters[
            REQUEST_TABLE_FILTERS.SUBMITTED_AT
          ] as RangeFilter,
          decisionDate: appliedFilters[
            REQUEST_TABLE_FILTERS.DECISION_DATE
          ] as RangeFilter,
          myRequests: (appliedFilters[REQUEST_TABLE_FILTERS.MY_REQUESTS] ||
            (!canBusinessUserSeeTeamRequests && isBusinessUser)) as boolean,
          archived: appliedFilters[REQUEST_TABLE_FILTERS.ARCHIVED] as boolean,
        },
      })
    );

    const filterKeys = Object.keys(appliedFilters);
    const hasStandardFilters = filterKeys.some(
      (key) => !QUICK_FILTERS.includes(key)
    );

    setAreStandardFiltersApplied(hasStandardFilters);
    setAreFiltersApplied(filterKeys.length > 0);
  }, [
    appliedFilters,
    dispatch,
    canBusinessUserSeeTeamRequests,
    isBusinessUser,
    statuses,
  ]);

  // Keeps "select all" covering rows that arrive from later pages. Uses the
  // updater form and omits `selectedRowsIds` from the deps on purpose: listing
  // the value this effect also sets made it re-run on its own output, and the
  // `includes` scan inside was O(rows x selected) on an unbounded list.
  useEffect(() => {
    if (!selectAll) return;

    setSelectedRowsIds((previous) => {
      const previousIds = new Set(previous);
      const newIds = requests
        .map((request) => request.id)
        .filter((id) => !previousIds.has(id));

      return newIds.length > 0 ? [...previous, ...newIds] : previous;
    });
  }, [selectAll, requests]);

  const handleSaveFiltersSettings = async (filters: RequestFilters) => {
    if (filtersSettings?.id) {
      await dispatch(
        updateFilterSetting({
          id: filtersSettings.id,
          updateFields: { filter_settings: filters },
        })
      );
    } else {
      await dispatch(
        createFilterSetting({
          filter_type: SETTINGS_FILTERS.REQUESTS_TABLE,
          filter_settings: filters,
        })
      );
    }
    setAreFiltersLoading(false);
  };

  const toggleFilters = () => setShowFilters((prev) => !prev);

  const handleUpdateFilters = (filters: RequestFilters) => {
    const filterKeys = Object.keys(filters);
    const hasFilters = filterKeys.length > 0;
    const hasStandardFilters = filterKeys.some(
      (key) => !QUICK_FILTERS.includes(key)
    );

    setAreFiltersApplied(hasFilters);
    setAreStandardFiltersApplied(hasStandardFilters);
    setAreFiltersLoading(true);
    handleSaveFiltersSettings(filters);
  };

  const canExport = (canExport: boolean) => {
    setCanExportRequests(canExport);
  };

  return (
    <div className='mx-6' data-testid='requests-table'>
      <RequestsSidePanelFilters
        filters={appliedFilters}
        onClose={toggleFilters}
        open={showFilters}
        onUpdateFilters={handleUpdateFilters}
      />
      <RequestsTableOperationHeader
        handleUpdateFilters={handleUpdateFilters}
        appliedFilters={appliedFilters}
        onSearchChange={searchRequests}
        showFilters={showFilters}
        toggleFilters={toggleFilters}
        showMyRequestsFilterButton={
          canBusinessUserSeeTeamRequests || !isBusinessUser
        }
        showQuickFilters
        canExportRequests={canExportRequests}
        selectedRowsIds={selectedRowsIds}
      />
      <RequestsTableComponent
        requests={requests}
        lastPatchedRequestId={lastPatchedRequestId}
        status={status}
        canFetchMore={canFetchMore}
        onFetchMore={onFetchMore}
        isSearchApplied={isSearchApplied}
        areFiltersApplied={areFiltersApplied}
        areStandardFiltersApplied={areStandardFiltersApplied}
        areFiltersLoading={areFiltersLoading}
        canSelectRows
        canExport={canExport}
        selectedRowsIds={selectedRowsIds}
        setSelectedRowsIds={setSelectedRowsIds}
      />
      <RequestsTableFooter total={totalCount} loaded={loadedCount} />
    </div>
  );
};

export default RequestsTable;
